import { App as AntApp, ConfigProvider } from 'antd';
import enGB from 'antd/locale/en_GB';
import frCA from 'antd/locale/fr_CA';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { DocumentTitle } from './a11y/useDocumentTitle';
import { AdvisorApplicationPage } from './advisor/AdvisorApplicationPage';
import { AdvisorApplicationStartPage } from './advisor/AdvisorApplicationStartPage';
import { AdvisorEntryPage } from './advisor/AdvisorEntryPage';
import { WorkflowReview } from './approver/WorkflowReview';
import { AuthGuard } from './auth/AuthGuard';
import { DevLoginPage } from './auth/DevLoginPage';
import { LoggedOutPage } from './auth/LoggedOutPage';
import { LogoutPage } from './auth/LogoutPage';
import { RequireRole, defaultRouteForRole } from './auth/RequireRole';
import { useRoles } from './auth/useRoles';
import { AppShell } from './components/layout/AppShell';
import { MessageBridge } from './components/MessageBridge';
import { CustomElementEditorPage } from './originator/customElements/CustomElementEditorPage';
import { CustomElementSelectorPage } from './originator/customElements/CustomElementSelectorPage';
import { DynamicFormBuilderPage } from './originator/form-builder/DynamicFormBuilderPage';
import { PdfFieldMappingPage } from './originator/PdfFieldMappingPage';
import { PreviewPage } from './originator/PreviewPage';
import { StepConfigPage } from './originator/StepConfigPage';
import { VersionDetailsPage } from './originator/VersionDetailsPage';
import { VersionHistoryPage } from './originator/VersionHistoryPage';
import { WorkflowDetailsPage } from './originator/WorkflowDetailsPage';
import { AdvisorDashboard } from './pages/AdvisorDashboard';
import { ApproverDashboard } from './pages/ApproverDashboard';
import { ArchivePage } from './pages/ArchivePage';
import { AuditHistoryPage } from './pages/AuditHistoryPage';
import { AuthErrorPage } from './pages/AuthErrorPage';
import { HealthPage } from './pages/HealthPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { OriginatorDashboard } from './pages/OriginatorDashboard';
import { antTheme } from './theme/theme';

/**
 * Redirects an authenticated user to their role's default dashboard. Query
 * string is preserved on every hop — an advisor arriving here from ISAM
 * login (bmo-workflow-platform-spec.md:35) needs `applicationType` /
 * `applicationId` / `mode` / `language` to survive the redirect. When an
 * advisor carries `applicationType`, route to the resolver at /advisor/entry
 * instead of the plain dashboard so they land directly on their workflow form
 * rather than the picker. `applicationId` is deliberately NOT part of this
 * check: it is nullable by contract (ISAM sends it empty when the advisor has
 * no existing application, and BuildContextRedirectUrl drops empty values), and
 * AdvisorEntryPage handles its absence by minting a fresh application. Gating on
 * it here sent those advisors to the picker with their applicationType silently
 * discarded. AdvisorEntryPage still falls back to the picker on its own when
 * applicationType can't be resolved.
 */
export function RoleHome() {
  const { role, hasDualRole } = useRoles();
  const location = useLocation();
  if (hasDualRole) return <Navigate to="/auth/error?cause=dual-role" replace />;
  if (!role) return <Navigate to="/auth/error?cause=no-role" replace />;
  const params = new URLSearchParams(location.search);
  const target =
    role === 'Advisor' && params.get('applicationType')
      ? '/advisor/entry'
      : defaultRouteForRole(role);
  return <Navigate to={`${target}${location.search}`} replace />;
}

export function App() {
  const { i18n } = useTranslation();
  const antLocale = i18n.language.startsWith('fr') ? frCA : enGB;

  return (
    <ConfigProvider
      locale={antLocale}
      theme={antTheme}
      // Render the required marker as a red asterisk *after* the label.
      form={{
        requiredMark: (label, { required }) => (
          <>
            {label}
            {required && <span className="bmo-req">*</span>}
          </>
        ),
      }}
    >
      <AntApp>
        <MessageBridge />
        {/* Mounted inside the Router (needs useLocation) but outside <Routes>,
            so one hook covers every route rather than 30 pages each having to
            remember (WCAG 2.4.2). */}
        <DocumentTitle />
        <Routes>
          {/* Public, no session required */}
          <Route element={<AppShell />}>
            <Route path="/health" element={<HealthPage />} />
            <Route path="/auth/logout" element={<LogoutPage />} />
            <Route path="/auth/logged-out" element={<LoggedOutPage />} />
            <Route path="/auth/dev-login" element={<DevLoginPage />} />
            <Route path="/auth/error" element={<AuthErrorPage />} />
          </Route>

          {/* Protected — auto-redirects to the appropriate BFF login when not signed in */}
          <Route
            element={
              <AuthGuard>
                <AppShell />
              </AuthGuard>
            }
          >
            <Route path="/" element={<RoleHome />} />

            {/* Approver-gated routes. `<RequireRole role="Approver"/>` renders
                <Outlet/> when the role check passes, else redirects. */}
            <Route element={<RequireRole role="Approver" />}>
              <Route path="/approver/dashboard" element={<ApproverDashboard />} />
              <Route path="/approver/audit-history" element={<AuditHistoryPage />} />
              <Route path="/approver/archive" element={<ArchivePage />} />
              <Route path="/approver/workflows/:id" element={<WorkflowReview />} />
              <Route path="/approver/workflows/:id/versions" element={<VersionHistoryPage />} />
              <Route path="/approver/workflows/:id/versions/:v" element={<VersionDetailsPage />} />
              <Route
                path="/approver/workflows/:id/steps/:stepId/config"
                element={<StepConfigPage />}
              />
              {/* Approver-side review preview — same component as the Originator
                  path. Lets the reviewer click "Preview" on any step row in the
                  review screen and land on that step's runtime preview. */}
              <Route path="/approver/workflows/:id/preview" element={<PreviewPage />} />
            </Route>

            {/* Originator-gated routes. */}
            <Route element={<RequireRole role="Originator" />}>
              <Route path="/originator/dashboard" element={<OriginatorDashboard />} />
              <Route path="/originator/audit-history" element={<AuditHistoryPage />} />
              <Route path="/originator/archive" element={<ArchivePage />} />
              <Route path="/originator/custom-elements" element={<CustomElementSelectorPage />} />
              <Route
                path="/originator/custom-elements/:id/edit"
                element={<CustomElementEditorPage />}
              />
              <Route path="/originator/workflows/:id" element={<WorkflowDetailsPage />} />
              <Route path="/originator/workflows/:id/versions" element={<VersionHistoryPage />} />
              <Route
                path="/originator/workflows/:id/versions/:v"
                element={<VersionDetailsPage />}
              />
              <Route
                path="/originator/workflows/:id/steps/:stepId/edit"
                element={<DynamicFormBuilderPage />}
              />
              <Route
                path="/originator/workflows/:id/steps/:stepId/config"
                element={<StepConfigPage />}
              />
              <Route
                path="/originator/workflows/:id/pdf-mapping"
                element={<PdfFieldMappingPage />}
              />
              <Route path="/originator/workflows/:id/preview" element={<PreviewPage />} />
            </Route>

            {/* Advisor-gated routes — pretend-advisor runtime for SFA-3.
                See docs/pretend-advisor.md for the swap-point index.
                `/new` mints a client-side applicationId (crypto.randomUUID)
                then hard-redirects to the ID-scoped URL so each browser tab
                is naturally isolated by React remount-per-URL. */}
            <Route element={<RequireRole role="Advisor" />}>
              <Route path="/advisor/dashboard" element={<AdvisorDashboard />} />
              <Route path="/advisor/entry" element={<AdvisorEntryPage />} />
              <Route
                path="/advisor/workflows/:templateId/applications/new"
                element={<AdvisorApplicationStartPage />}
              />
              <Route
                path="/advisor/workflows/:templateId/applications/:applicationId"
                element={<AdvisorApplicationPage />}
              />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </AntApp>
    </ConfigProvider>
  );
}
